if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia("(min-width: 750px)");

        // Initialize variant media filtering
        if (this.dataset.hideVariants === "true") {
          this.initVariantMediaFilter();
        }

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener(
          "slideChanged",
          debounce(this.onSlideChanged.bind(this), 500)
        );
        this.elements.thumbnails
          .querySelectorAll("[data-target]")
          .forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector("button")
              .addEventListener(
                "click",
                this.setActiveMedia.bind(
                  this,
                  mediaToSwitch.dataset.target,
                  false
                )
              );
          });
        if (
          this.dataset.desktopLayout.includes("thumbnail") &&
          this.mql.matches
        )
          this.removeListSemantic();
      }

      /* ── Variant media filtering (alt-text matching) ── */

      initVariantMediaFilter() {
        const sectionId = this.id.replace("MediaGallery-", "");
        const mapEl = document.getElementById(`VariantMediaMap-${sectionId}`);
        if (!mapEl) return;

        let config;
        try {
          config = JSON.parse(mapEl.textContent);
        } catch (e) {
          return;
        }

        // New format: { colorOptionIndex, allColorValues, variants }
        if (config.variants) {
          this.colorOptionIndex = config.colorOptionIndex || 0;
          this.allColorValues = new Set(
            (config.allColorValues || []).map((v) => v.toLowerCase().trim())
          );
          this.variantsConfig = config.variants;
        } else {
          // Not expected, but bail gracefully
          return;
        }

        // Get initial selected variant from URL or first variant
        const urlParams = new URLSearchParams(window.location.search);
        const urlVariantId = urlParams.get("variant");

        if (urlVariantId && this.variantsConfig[urlVariantId]) {
          this.filterMediaForVariant(urlVariantId, sectionId);
        } else {
          // Use first variant
          const firstId = Object.keys(this.variantsConfig)[0];
          if (firstId) this.filterMediaForVariant(firstId, sectionId);
        }
      }

      /**
       * Public method for variant picker to call directly.
       */
      filterForVariantId(variantId) {
        if (this.dataset.hideVariants !== "true") return;
        const sectionId = this.id.replace("MediaGallery-", "");
        this.filterMediaForVariant(String(variantId), sectionId);
      }

      /**
       * Core filter logic — alt-text matching.
       *
       * SHOW: media.alt matches selected color (case-insensitive)
       * SHOW: media.alt is empty OR doesn't match ANY known color value (shared)
       * HIDE: media.alt matches a DIFFERENT known color value
       */
      filterMediaForVariant(variantId, sectionId) {
        if (!this.variantsConfig) return;
        if (!sectionId) sectionId = this.id.replace("MediaGallery-", "");

        const variantConfig = this.variantsConfig[variantId];
        if (!variantConfig) return;

        const colorKey = `option${this.colorOptionIndex + 1}`;
        const selectedColor = variantConfig[colorKey];
        if (!selectedColor) return;

        const selectedColorLower = selectedColor.toLowerCase().trim();

        const applyFilter = (el, altAttr) => {
          const alt = (altAttr || "").toLowerCase().trim();

          if (!alt) {
            // No alt text — shared/lifestyle image, always show
            el.classList.remove("variant-media-hidden");
            return;
          }

          if (alt === selectedColorLower) {
            // Matches selected color — show
            el.classList.remove("variant-media-hidden");
            return;
          }

          if (this.allColorValues.has(alt)) {
            // Matches a DIFFERENT color — hide
            el.classList.add("variant-media-hidden");
            return;
          }

          // Alt text doesn't match any known color — treat as shared, show
          el.classList.remove("variant-media-hidden");
        };

        // Filter gallery slides
        this.elements.viewer.querySelectorAll("[data-media-id]").forEach((slide) => {
          applyFilter(slide, slide.dataset.mediaAlt);
        });

        // Filter thumbnails
        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll("[data-target]").forEach((thumb) => {
            applyFilter(thumb, thumb.dataset.mediaAlt);
          });
        }

        // Filter modal media if it exists
        const modal = document.getElementById(`ProductModal-${sectionId}`);
        if (modal) {
          modal.querySelectorAll("[data-modal-slide]").forEach((item) => {
            applyFilter(item, item.dataset.mediaAlt);
          });
        }

        // Update slide counter
        this._updateSliderCounter();
      }

      _updateSliderCounter() {
        const counterTotal = this.elements.viewer
          ?.closest("slider-component")
          ?.querySelector(".slider-counter--total");
        if (counterTotal) {
          const visible = this.elements.viewer.querySelectorAll(
            "[data-media-id]:not(.variant-media-hidden)"
          );
          counterTotal.textContent = visible.length;
        }
      }

      /* ── Standard gallery methods ── */

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(
          `[data-media-id="${mediaId}"]`
        );
        if (!activeMedia) return;

        this.elements.viewer
          .querySelectorAll("[data-media-id]")
          .forEach((element) => {
            element.classList.remove("is-active");
          });
        activeMedia.classList.add("is-active");

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(
              `[data-target="${mediaId}"]`
            );
            if (activeThumbnail)
              activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({
              left: activeMedia.offsetLeft,
            });
          }
          if (
            !this.elements.thumbnails ||
            this.dataset.desktopLayout === "stacked"
          ) {
            activeMedia.scrollIntoView({ behavior: "smooth" });
          }
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${mediaId}"]`
        );
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(
          activeMedia,
          activeThumbnail.dataset.mediaPosition
        );
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll("button")
          .forEach((element) => element.removeAttribute("aria-current"));
        thumbnail.querySelector("button").setAttribute("aria-current", true);

        if (
          typeof this.elements.thumbnails.isSlideVisible === "function" &&
          this.elements.thumbnails.isSlideVisible(thumbnail, 10)
        ) {
          return;
        }

        if (this.elements.thumbnails.slider) {
          this.elements.thumbnails.slider.scrollTo({
            left: thumbnail.offsetLeft,
          });
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector(
          ".product__modal-opener--image img"
        );
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute("aria-hidden", false);
          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace(
              "[index]",
              position
            );
          setTimeout(() => {
            this.elements.liveRegion.setAttribute("aria-hidden", true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector(".deferred-media");
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader || document.querySelector("sticky-header");
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event("preventHeaderReveal"));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute("role", "presentation");
        this.elements.viewer.sliderItems.forEach((slide) =>
          slide.setAttribute("role", "presentation")
        );
      }
    }
  );
}
